import flatten from 'lodash/flatten';
import {GeneratedList, Reference} from '@/model/types';
import {createConditionsReducer} from '../document-model/reducers';
import {ModelFactory} from '../types';
import {recursiveReplaceIndex} from '../utils/object';
import {createConditionFactory, getConditionDependencies} from './condition';
import {createFactoryFromSwitch, CreateFactoryFromSwitchOptions, NoOp} from './switch';

export const createFactoryFromRepeat = <T, U, V>(
  {$repeat, value}: Extract<GeneratedList<T>['$generated'], {$repeat: unknown}>,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ModelFactory<V[]> => {
  if (!('$query' in $repeat)) {
    dependencyRegistrer?.('visibility', $repeat.$ref);
  } else {
    dependencyRegistrer?.('visibility', $repeat.$query);
  }

  return (valueResolver, visibilitySet) => {
    const repeat = Number(valueResolver($repeat));

    return Array.from({length: repeat}).map((_, index) => {
      let factory: ModelFactory<V>;

      if (value && typeof value === 'object' && '$switch' in value) {
        factory = createFactoryFromSwitch(recursiveReplaceIndex(value, index), undefined as V, {
          process,
          transform,
          dependencyRegistrer,
        });
      } else {
        const processedValue = process(recursiveReplaceIndex(value, index));
        factory = resolver => transform(processedValue, resolver);
      }
      return factory(valueResolver, visibilitySet);
    });
  };
};

export const createFactoryFromEach = <T, U = T, V = U>(
  {$each, value, useValueAsIndex = false}: Extract<GeneratedList<T>['$generated'], {$each: unknown}>,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ModelFactory<V[]> => {
  dependencyRegistrer?.('visibility', $each.$query);

  $each.filters?.forEach(filter => {
    if (filter.operator === 'and' || filter.operator === 'or' || filter.operator === 'not') {
      dependencyRegistrer?.('visibility', ...getConditionDependencies(filter));
    }
  });

  return (valueResolver, visibilitySet) => {
    const resolvedEach = valueResolver<(string | Reference)[][]>({resolve: false, ...$each});
    const keys = flatten(resolvedEach);
    return keys.map(result => {
      const ref = typeof result === 'string' ? result : result.$ref;
      const index = useValueAsIndex ? ref : Number(ref.split('#')[1]);

      let factory: ModelFactory<V>;

      if (value && typeof value === 'object' && '$switch' in value) {
        factory = createFactoryFromSwitch(recursiveReplaceIndex(value, index), undefined as V, {
          process,
          transform,
          dependencyRegistrer,
        });
      } else {
        const processedValue = process(recursiveReplaceIndex(value, index));
        factory = resolver => transform(processedValue, resolver);
      }
      return factory(valueResolver, visibilitySet);
    });
  };
};

export const createFactoryFromList = <T, U, V>(
  generatedObj: Extract<GeneratedList<T>['$generated'], {$list: unknown}>,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ModelFactory<V[]> => {
  const {$list} = generatedObj;

  const mapped = $list.map(item => {
    if (item && typeof item === 'object' && '$if' in item) {
      return {
        $if: item.$if.map(condition => createConditionFactory(condition, dependencyRegistrer, 'visibility')),
        $then: item.$then,
      };
    }

    return {$if: [() => () => true], $then: item};
  });

  return (valueResolver, visibilitySet) => {
    const conditionReducer = createConditionsReducer(valueResolver, visibilitySet);
    return mapped
      .filter(({$if}) => conditionReducer($if))
      .map(({$then}, index) => transform(process(recursiveReplaceIndex($then as T, index)), valueResolver));
  };
};

export const createFactoryFromGeneratedList = <T, U = T, V = U>(
  {$generated}: GeneratedList<T>,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ModelFactory<V[]> => {
  if ('$repeat' in $generated) {
    return createFactoryFromRepeat($generated, {
      process,
      transform,
      dependencyRegistrer,
    });
  }

  if ('$each' in $generated) {
    return createFactoryFromEach($generated, {
      process,
      transform,
      dependencyRegistrer,
    });
  }

  return createFactoryFromList($generated, {
    process,
    transform,
    dependencyRegistrer,
  });
};
